import {
  ActionIcon,
  Alert,
  Button,
  FileButton,
  Modal,
  MultiSelect,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCalculator,
  IconChevronRight,
  IconCircleCheck,
  IconDatabase,
  IconDeviceFloppy,
  IconFileText,
  IconPlus,
  IconSearch,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { Controller, type FieldErrors, type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useShell, useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { fileSize } from '../pms/AttachmentCard.js';
import classes from './Fuel.module.css';
import { OpeningBalanceModal } from './OpeningBalanceModal.js';
import type { FuelSource } from './source.js';
import { FUEL_TYPES_PATH } from './tabs.js';
import {
  detailIssues,
  EMPTY_FUEL_TYPE,
  EMPTY_TANK,
  type FuelTypeFormValues,
  fuelTypeFormIssues,
  fuelTypeFormOf,
  fuelTypeInputOf,
  matchesTank,
  TANK_DESCRIPTION_MAX,
  type TankFormValues,
  tankFormIssues,
  tankFormOf,
  tankInputOf,
} from './tank-form.js';
import type { FuelTanksData, FuelType, TankTableChange, TankTableFile } from './types.js';

const ROLES = ['storage', 'settling', 'service', 'overflow', 'other'] as const;
const LOCATIONS = ['port', 'starboard', 'centre'] as const;
const METHODS = ['sounding', 'ullage', 'sensor'] as const;
const CATEGORIES = [
  'residual',
  'distillate',
  'lng',
  'lpg',
  'methanol',
  'ethanol',
  'biofuel',
  'other',
] as const;
const FACTOR_CLASSES = [
  'HFO',
  'LFO',
  'MDO_MGO',
  'LNG_OTTO_MS',
  'LNG_OTTO_SS',
  'LNG_DIESEL',
  'LPG_PROPANE',
  'LPG_BUTANE',
  'METHANOL',
  'ETHANOL',
  'OTHER',
] as const;

/** Domain şemasının alan kodlarını React Hook Form hatasına çevirir (sunucu yeniden denetler). */
function issueResolver<T extends object>(
  issuesOf: (values: T) => Partial<Record<keyof T, string>>,
): Resolver<T> {
  return async (values) => {
    const issues = issuesOf(values);
    const keys = Object.keys(issues) as (keyof T)[];
    if (keys.length === 0) return { values, errors: {} };
    return {
      values: {},
      errors: Object.fromEntries(
        keys.map((key) => [key, { type: 'validate', message: issues[key] }]),
      ) as FieldErrors<T>,
    };
  };
}

/** Hata metni: `fuel.errors.<KOD>`, yoksa genel ileti. */
function useFuelError() {
  const { t } = useTranslation();
  return (error: unknown) =>
    t(`fuel.errors.${errorCode(error)}`, { defaultValue: t('fuel.errors.GENERIC') });
}

type TableState = { kind: 'keep' } | { kind: 'new'; file: File } | { kind: 'remove' };
const KEEP: TableState = { kind: 'keep' };

export interface TanksPanelProps {
  source: FuelSource;
  data: FuelTanksData;
  subtab: 'definitions' | 'fuelTypes';
  onReload: () => Promise<FuelTanksData | null>;
}

/**
 * Tanklar sekmesi (ortağın 02-tanklar.jpeg tasarımı): Tank Tanımları ve Yakıt Türleri alt
 * sekmeleri. Alt sekme yoldan gelir (`/fuel/tanks`, `/fuel/tanks/fuel-types`).
 */
export function TanksPanel({ source, data, subtab: initial, onReload }: TanksPanelProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const [subtab, setSubtab] = useState(initial);
  const id = useId();
  useEffect(() => setSubtab(initial), [initial]);
  const choose = (next: 'definitions' | 'fuelTypes') => {
    setSubtab(next);
    shell?.navigate(next === 'fuelTypes' ? FUEL_TYPES_PATH : '/fuel/tanks');
  };
  const tab = (key: 'definitions' | 'fuelTypes') => (
    <button
      type="button"
      role="tab"
      id={`${id}-${key}`}
      aria-selected={subtab === key}
      aria-controls={`${id}-panel`}
      tabIndex={subtab === key ? 0 : -1}
      className={classes.subtab}
      onClick={() => choose(key)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          choose(key === 'definitions' ? 'fuelTypes' : 'definitions');
        }
      }}
    >
      {t(`fuel.tanks.${key}`)}
    </button>
  );
  return (
    <section className={classes.card} aria-label={t('fuel.tabs.tanks')}>
      <div className={classes.subtabs} role="tablist" aria-label={t('fuel.tanks.subtabsLabel')}>
        {tab('definitions')}
        {tab('fuelTypes')}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${subtab}`}>
        {subtab === 'definitions' ? (
          <TankDefinitions source={source} data={data} onReload={onReload} />
        ) : (
          <FuelTypesPanel source={source} data={data} onReload={onReload} />
        )}
      </div>
    </section>
  );
}

// ---------- Tank Tanımları ----------

function TankDefinitions({
  source,
  data,
  onReload,
}: {
  source: FuelSource;
  data: FuelTanksData;
  onReload: () => Promise<FuelTanksData | null>;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const errorText = useFuelError();
  const editor = data.scope === 'ship' ? source.tankEditor : undefined;
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(data.tanks[0]?.id ?? null);
  const [creating, setCreating] = useState(
    data.tanks.length === 0 && Boolean(editor) && data.can.create,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [opening, setOpening] = useState(false);
  const [tables, setTables] = useState<Record<'calibration' | 'trim_list', TableState>>({
    calibration: KEEP,
    trim_list: KEEP,
  });
  const selected = creating ? null : (data.tanks.find((tank) => tank.id === selectedId) ?? null);
  const readOnly =
    !editor || (creating ? !data.can.create : !data.can.edit) || (!creating && !selected);
  const form = useForm<TankFormValues>({
    defaultValues: selected ? tankFormOf(selected) : EMPTY_TANK,
    resolver: issueResolver(tankFormIssues),
  });
  const { errors, isDirty } = form.formState;
  const tablesDirty = tables.calibration.kind !== 'keep' || tables.trim_list.kind !== 'keep';
  useUnsavedChanges(!readOnly && (isDirty || tablesDirty));

  // Seçim ya da kayıt değişince form o tankın değerlerine döner
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız seçim ve sürüm değişiminde
  useEffect(() => {
    form.reset(selected ? tankFormOf(selected) : EMPTY_TANK);
    setTables({ calibration: KEEP, trim_list: KEEP });
  }, [selected?.id, selected?.rowVersion, creating]);

  const visible = data.tanks.filter((tank) => matchesTank(tank, query));
  const fieldError = (name: keyof TankFormValues) => {
    const code = errors[name]?.message;
    return code
      ? t(`fuel.fieldErrors.${code}`, { defaultValue: t('fuel.errors.VALIDATION_FAILED') })
      : undefined;
  };
  const fuelOptions = useMemo(
    () =>
      data.fuelTypes
        .filter((fuel) => fuel.active || selected?.allowedFuelTypeIds.includes(fuel.id))
        .map((fuel) => ({ value: fuel.id, label: fuel.code })),
    [data.fuelTypes, selected],
  );
  const consumerOptions = useMemo(
    () => data.consumers.map((c) => ({ value: c.id, label: c.tag ?? c.code })),
    [data.consumers],
  );
  const options = (keys: readonly string[], group: string) =>
    keys.map((key) => ({ value: key, label: t(`fuel.tanks.${group}.${key}`) }));

  const startNew = () => {
    setCreating(true);
    setSelectedId(null);
    setError(null);
    setSaved(false);
  };

  const cancel = () => {
    setError(null);
    if (creating && data.tanks.length > 0) {
      setCreating(false);
      setSelectedId(data.tanks[0]?.id ?? null);
      return;
    }
    form.reset(selected ? tankFormOf(selected) : EMPTY_TANK);
    setTables({ calibration: KEEP, trim_list: KEEP });
  };

  const submit = form.handleSubmit(async (values) => {
    if (!editor) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const changes: TankTableChange[] = [];
      for (const kind of ['calibration', 'trim_list'] as const) {
        const pending = tables[kind];
        if (pending.kind === 'new') {
          const stored = await editor.upload(pending.file);
          changes.push({ kind, file: { sha256: stored.sha256, fileName: pending.file.name } });
        } else if (pending.kind === 'remove') {
          changes.push({ kind, file: null });
        }
      }
      const result = await editor.save({
        ...(selected ? { id: selected.id, rowVersion: selected.rowVersion } : {}),
        tank: tankInputOf(values),
        ...(changes.length > 0 ? { tables: changes } : {}),
      });
      await onReload();
      setCreating(false);
      setSelectedId(result.id);
      setSaved(true);
    } catch (failure) {
      setError(failure);
      const detail = (failure as { detail?: unknown } | null)?.detail;
      for (const [field, code] of Object.entries(detailIssues(detail))) {
        if (field in EMPTY_TANK) {
          form.setError(field as keyof TankFormValues, { type: 'server', message: code });
        }
      }
    } finally {
      setBusy(false);
    }
  });

  const unitSection = (unit: string) => <span className={classes.unit}>{unit}</span>;
  const levelUnit = form.watch('levelUnit');
  const unitText = t(`fuel.tanks.units.${levelUnit === 'm3' ? 'm3' : 'pct'}`);
  const description = form.watch('description');
  const base = useId();
  const ids = {
    capacity: `${base}-capacity`,
    fill: `${base}-fill`,
    unusable: `${base}-unusable`,
    low: `${base}-low`,
    high: `${base}-high`,
    method: `${base}-method`,
    active: `${base}-active`,
  };

  const row = (label: string, htmlFor: string, required: boolean, control: ReactNode) => (
    <div className={classes.row}>
      <label htmlFor={htmlFor} className={classes.rowLabel}>
        {label}
        {required ? (
          <span className={classes.required} aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {control}
    </div>
  );

  const decimalInput = (
    name: 'capacityM3' | 'fillLimitPct' | 'unusableM3' | 'lowLevel' | 'highLevel',
    id: string,
    required: boolean,
    section: ReactNode,
  ) => (
    <TextInput
      id={id}
      inputMode="decimal"
      readOnly={readOnly}
      required={required}
      rightSection={section}
      rightSectionWidth={56}
      rightSectionPointerEvents={name === 'lowLevel' || name === 'highLevel' ? 'all' : 'none'}
      error={fieldError(name)}
      {...form.register(name)}
    />
  );

  const levelUnitSelect = readOnly ? (
    unitSection(unitText)
  ) : (
    <select
      className={classes.unitSelect}
      aria-label={t('fuel.tanks.fields.levelUnit')}
      {...form.register('levelUnit')}
    >
      <option value="pct">{t('fuel.tanks.units.pct')}</option>
      <option value="m3">{t('fuel.tanks.units.m3')}</option>
    </select>
  );

  const tableField = (kind: 'calibration' | 'trim_list', label: string) => {
    const current: TankTableFile | null =
      kind === 'calibration' ? (selected?.calibration ?? null) : (selected?.trimList ?? null);
    const pending = tables[kind];
    const shown =
      pending.kind === 'new'
        ? { name: pending.file.name, size: pending.file.size, meta: t('fuel.tanks.file.pending') }
        : pending.kind === 'keep' && current
          ? {
              name: current.fileName,
              size: current.size,
              meta: null,
            }
          : null;
    return (
      <div className={classes.stacked}>
        <span className={classes.rowLabel}>{label}</span>
        {shown ? (
          <div className={classes.file}>
            <IconFileText size={26} stroke={1.6} className={classes.fileIcon} aria-hidden />
            <div className={classes.fileText}>
              <span className={classes.fileName} title={shown.name}>
                {shown.name}
              </span>
              <span className={classes.fileMeta}>
                {fileSize(shown.size, language)}
                {shown.meta ? ` · ${shown.meta}` : ''}
              </span>
            </div>
            {readOnly ? null : (
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={t('fuel.tanks.file.remove', { name: shown.name })}
                onClick={() =>
                  setTables((prev) => ({
                    ...prev,
                    [kind]: pending.kind === 'new' && !current ? KEEP : { kind: 'remove' },
                  }))
                }
              >
                <IconX size={18} />
              </ActionIcon>
            )}
          </div>
        ) : (
          <div className={classes.fileEmpty}>
            <span>
              {pending.kind === 'remove' ? t('fuel.tanks.file.removed') : t('fuel.tanks.file.none')}
            </span>
            {readOnly ? null : (
              <FileButton
                accept="application/pdf,image/jpeg,image/png"
                onChange={(file) => {
                  if (file) setTables((prev) => ({ ...prev, [kind]: { kind: 'new', file } }));
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    ml="auto"
                    leftSection={<IconUpload size={14} />}
                    aria-label={`${t('fuel.tanks.file.upload')}: ${label}`}
                  >
                    {t('fuel.tanks.file.upload')}
                  </Button>
                )}
              </FileButton>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className={classes.tankLayout}>
        <aside className={classes.list} aria-label={t('fuel.tanks.listLabel')}>
          {editor && data.can.create ? (
            <Button fullWidth size="md" leftSection={<IconPlus size={18} />} onClick={startNew}>
              {t('fuel.tanks.add')}
            </Button>
          ) : null}
          <TextInput
            placeholder={t('fuel.tanks.search')}
            aria-label={t('fuel.tanks.searchLabel')}
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
          {data.tanks.length === 0 ? (
            <p className={classes.listNote}>
              {editor ? t('fuel.tanks.emptyHint') : t('fuel.tanks.emptyOffice')}
            </p>
          ) : visible.length === 0 ? (
            <p className={classes.listNote}>{t('fuel.tanks.noMatch')}</p>
          ) : (
            <ul className={classes.items}>
              {visible.map((tank) => (
                <li key={tank.id}>
                  <button
                    type="button"
                    className={classes.item}
                    aria-current={!creating && tank.id === selectedId ? 'true' : undefined}
                    onClick={() => {
                      setCreating(false);
                      setSelectedId(tank.id);
                      setError(null);
                      setSaved(false);
                    }}
                  >
                    <IconDatabase size={26} stroke={1.5} className={classes.itemIcon} aria-hidden />
                    <span className={classes.itemText}>
                      <span className={classes.itemName}>{tank.name}</span>
                      <span className={classes.itemCode}>
                        {tank.code}
                        {tank.active ? null : (
                          <span className={classes.itemBadge}>{t('fuel.tanks.inactive')}</span>
                        )}
                      </span>
                    </span>
                    <IconChevronRight size={18} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <div className={classes.form}>
          {error ? (
            <Alert
              className={classes.notice}
              mt="md"
              color="red"
              icon={<IconAlertCircle size={18} />}
              role="alert"
            >
              {errorText(error)}
            </Alert>
          ) : null}
          {saved ? (
            <Alert
              className={classes.notice}
              mt="md"
              color="green"
              icon={<IconCircleCheck size={18} />}
              role="status"
            >
              {t('fuel.tanks.saved')}
            </Alert>
          ) : null}
          {!editor ? (
            <Alert className={classes.notice} mt="md" color="blue" role="note">
              {t('fuel.tanks.readOnly')}
            </Alert>
          ) : null}
          {!creating && !selected ? null : (
            <div className={classes.formBody}>
              <h2 className={classes.formTitle}>
                {creating ? t('fuel.tanks.newTank') : t('fuel.tanks.info')}
              </h2>
              <div className={classes.left}>
                <TextInput
                  label={t('fuel.tanks.fields.code')}
                  withAsterisk
                  readOnly={readOnly}
                  error={fieldError('code')}
                  {...form.register('code')}
                />
                <TextInput
                  label={t('fuel.tanks.fields.name')}
                  withAsterisk
                  readOnly={readOnly}
                  error={fieldError('name')}
                  {...form.register('name')}
                />
                <Controller
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <Select
                      label={t('fuel.tanks.fields.role')}
                      withAsterisk
                      placeholder={t('fuel.tanks.select')}
                      readOnly={readOnly}
                      allowDeselect={false}
                      data={options(ROLES, 'roles')}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      onBlur={field.onBlur}
                      error={fieldError('role')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <Select
                      label={t('fuel.tanks.fields.location')}
                      withAsterisk
                      placeholder={t('fuel.tanks.select')}
                      readOnly={readOnly}
                      allowDeselect={false}
                      data={options(LOCATIONS, 'locations')}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      onBlur={field.onBlur}
                      error={fieldError('location')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="allowedFuelTypeIds"
                  render={({ field }) => (
                    <MultiSelect
                      className={classes.wide}
                      label={t('fuel.tanks.fields.allowedFuelTypeIds')}
                      withAsterisk
                      readOnly={readOnly}
                      data={fuelOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      nothingFoundMessage={t('fuel.tanks.noFuelTypes')}
                      error={fieldError('allowedFuelTypeIds')}
                      {...(fuelOptions.length === 0
                        ? { description: t('fuel.tanks.noFuelTypes') }
                        : {})}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="consumerIds"
                  render={({ field }) => (
                    <MultiSelect
                      className={classes.wide}
                      label={t('fuel.tanks.fields.consumerIds')}
                      readOnly={readOnly}
                      searchable
                      data={consumerOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldError('consumerIds')}
                    />
                  )}
                />
                <div className={classes.wide}>
                  <Textarea
                    label={t('fuel.tanks.fields.description')}
                    readOnly={readOnly}
                    minRows={3}
                    autosize
                    maxLength={TANK_DESCRIPTION_MAX}
                    error={fieldError('description')}
                    {...form.register('description')}
                  />
                  <div className={classes.counter} aria-hidden>
                    {(description ?? '').length}/{TANK_DESCRIPTION_MAX}
                  </div>
                </div>
              </div>
              <div className={classes.divider} aria-hidden />
              <div className={classes.right}>
                {row(
                  t('fuel.tanks.fields.capacityM3'),
                  ids.capacity,
                  true,
                  decimalInput('capacityM3', ids.capacity, true, unitSection('m³')),
                )}
                {row(
                  t('fuel.tanks.fields.fillLimitPct'),
                  ids.fill,
                  true,
                  decimalInput('fillLimitPct', ids.fill, true, unitSection('%')),
                )}
                {row(
                  t('fuel.tanks.fields.unusableM3'),
                  ids.unusable,
                  false,
                  decimalInput('unusableM3', ids.unusable, false, unitSection('m³')),
                )}
                {row(
                  t('fuel.tanks.fields.lowLevel', { unit: unitText }),
                  ids.low,
                  true,
                  decimalInput('lowLevel', ids.low, true, levelUnitSelect),
                )}
                {row(
                  t('fuel.tanks.fields.highLevel', { unit: unitText }),
                  ids.high,
                  false,
                  decimalInput('highLevel', ids.high, false, unitSection(unitText)),
                )}
                {row(
                  t('fuel.tanks.fields.measurementMethod'),
                  ids.method,
                  true,
                  <Controller
                    control={form.control}
                    name="measurementMethod"
                    render={({ field }) => (
                      <Select
                        id={ids.method}
                        placeholder={t('fuel.tanks.select')}
                        readOnly={readOnly}
                        allowDeselect={false}
                        data={options(METHODS, 'methods')}
                        value={field.value || null}
                        onChange={(value) => field.onChange(value ?? '')}
                        onBlur={field.onBlur}
                        error={fieldError('measurementMethod')}
                      />
                    )}
                  />,
                )}
                {tableField('calibration', t('fuel.tanks.fields.calibration'))}
                {tableField('trim_list', t('fuel.tanks.fields.trimList'))}
                {row(
                  t('fuel.tanks.fields.active'),
                  ids.active,
                  true,
                  <Controller
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <Switch
                        id={ids.active}
                        size="lg"
                        disabled={readOnly}
                        checked={field.value}
                        onChange={(event) => field.onChange(event.currentTarget.checked)}
                        label={field.value ? t('fuel.tanks.activeOn') : t('fuel.tanks.activeOff')}
                      />
                    )}
                  />,
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {editor ? (
        <div className={classes.footer}>
          {/* Açılış bakiyesi (ADR 0117 §13, Ek Y2): kaydedilmiş tankın ROB defteri başlangıcı */}
          <Button
            variant="default"
            size="md"
            leftSection={<IconCalculator size={18} />}
            disabled={creating || !selected || !source.ledgerEditor}
            title={creating || !selected ? t('fuel.opening.noTank') : undefined}
            onClick={() => setOpening(true)}
          >
            {t('fuel.tanks.openingBalance')}
          </Button>
          {readOnly ? null : (
            <div className={classes.footerActions}>
              <Button variant="default" size="md" onClick={cancel} disabled={busy}>
                {t('fuel.tanks.cancel')}
              </Button>
              <Button
                type="submit"
                size="md"
                loading={busy}
                leftSection={<IconDeviceFloppy size={18} />}
              >
                {t('fuel.tanks.save')}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      <OpeningBalanceModal
        source={source}
        tank={selected}
        vesselId={data.scope === 'web' ? data.vessel.id : null}
        opened={opening}
        onClose={() => setOpening(false)}
      />
    </form>
  );
}

// ---------- Yakıt Türleri ----------

function FuelTypesPanel({
  source,
  data,
  onReload,
}: {
  source: FuelSource;
  data: FuelTanksData;
  onReload: () => Promise<FuelTanksData | null>;
}) {
  const { t } = useTranslation();
  const editor = data.scope === 'web' && data.can.fuelTypes ? source.typeEditor : undefined;
  const [editing, setEditing] = useState<FuelType | 'new' | null>(null);
  const [saved, setSaved] = useState(false);
  return (
    <div>
      <div className={classes.typesHead}>
        <p className={classes.typesNote}>
          {data.scope === 'ship' ? t('fuel.fuelTypes.shipNote') : t('fuel.fuelTypes.emptyHint')}
        </p>
        {editor ? (
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setSaved(false);
              setEditing('new');
            }}
          >
            {t('fuel.fuelTypes.add')}
          </Button>
        ) : null}
      </div>
      {saved ? (
        <Alert mx="lg" mt="md" color="green" icon={<IconCircleCheck size={18} />} role="status">
          {t('fuel.fuelTypes.saved')}
        </Alert>
      ) : null}
      <div className={classes.tableWrap}>
        {data.fuelTypes.length === 0 ? (
          <p className={classes.listNote}>{t('fuel.fuelTypes.empty')}</p>
        ) : (
          <table className={classes.table} aria-label={t('fuel.fuelTypes.listLabel')}>
            <thead>
              <tr>
                <th scope="col">{t('fuel.fuelTypes.columns.code')}</th>
                <th scope="col">{t('fuel.fuelTypes.columns.name')}</th>
                <th scope="col">{t('fuel.fuelTypes.columns.category')}</th>
                <th scope="col">{t('fuel.fuelTypes.columns.grade')}</th>
                <th scope="col">{t('fuel.fuelTypes.columns.factorClass')}</th>
                <th scope="col">{t('fuel.fuelTypes.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.fuelTypes.map((fuel) => (
                <tr key={fuel.id}>
                  <td>
                    {editor ? (
                      <button
                        type="button"
                        className={classes.rowButton}
                        aria-label={t('fuel.fuelTypes.edit', { code: fuel.code })}
                        onClick={() => {
                          setSaved(false);
                          setEditing(fuel);
                        }}
                      >
                        {fuel.code}
                      </button>
                    ) : (
                      <strong>{fuel.code}</strong>
                    )}
                  </td>
                  <td>{fuel.name}</td>
                  <td>{t(`fuel.fuelTypes.categories.${fuel.category}`, fuel.category)}</td>
                  <td>{fuel.grade ?? '—'}</td>
                  <td>
                    {fuel.factorClass
                      ? t(`fuel.fuelTypes.factorClasses.${fuel.factorClass}`, fuel.factorClass)
                      : t('fuel.fuelTypes.unverified')}
                  </td>
                  <td>{fuel.active ? t('fuel.fuelTypes.active') : t('fuel.fuelTypes.inactive')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editor && editing ? (
        <FuelTypeDialog
          fuel={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            await editor.save({
              ...(editing === 'new' ? {} : { id: editing.id, rowVersion: editing.rowVersion }),
              fuelType: fuelTypeInputOf(values),
            });
            await onReload();
            setEditing(null);
            setSaved(true);
          }}
        />
      ) : null}
    </div>
  );
}

function FuelTypeDialog({
  fuel,
  onClose,
  onSave,
}: {
  fuel: FuelType | null;
  onClose: () => void;
  onSave: (values: FuelTypeFormValues) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useFuelError();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<FuelTypeFormValues>({
    defaultValues: fuel ? fuelTypeFormOf(fuel) : EMPTY_FUEL_TYPE,
    resolver: issueResolver(fuelTypeFormIssues),
  });
  const { errors, isSubmitting } = form.formState;
  const fieldError = (name: keyof FuelTypeFormValues) => {
    const code = errors[name]?.message;
    return code
      ? t(`fuel.fieldErrors.${code}`, { defaultValue: t('fuel.errors.VALIDATION_FAILED') })
      : undefined;
  };
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSave(values);
    } catch (failure) {
      setError(failure);
      const detail = (failure as { detail?: unknown } | null)?.detail;
      for (const [field, code] of Object.entries(detailIssues(detail))) {
        if (field in EMPTY_FUEL_TYPE) {
          form.setError(field as keyof FuelTypeFormValues, { type: 'server', message: code });
        }
      }
    }
  });
  return (
    <Modal
      opened
      onClose={onClose}
      title={t('fuel.fuelTypes.title')}
      size="lg"
      closeButtonProps={{ 'aria-label': t('fuel.fuelTypes.close') }}
    >
      <form onSubmit={submit} noValidate>
        <div className={classes.left}>
          {error ? (
            <Alert
              className={classes.wide}
              color="red"
              icon={<IconAlertCircle size={18} />}
              role="alert"
            >
              {errorText(error)}
            </Alert>
          ) : null}
          <TextInput
            label={t('fuel.fuelTypes.fields.code')}
            withAsterisk
            readOnly={fuel !== null}
            error={fieldError('code')}
            {...form.register('code')}
          />
          <TextInput
            label={t('fuel.fuelTypes.fields.name')}
            withAsterisk
            error={fieldError('name')}
            {...form.register('name')}
          />
          <Controller
            control={form.control}
            name="category"
            render={({ field }) => (
              <Select
                label={t('fuel.fuelTypes.fields.category')}
                withAsterisk
                allowDeselect={false}
                data={CATEGORIES.map((c) => ({
                  value: c,
                  label: t(`fuel.fuelTypes.categories.${c}`),
                }))}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('category')}
              />
            )}
          />
          <TextInput
            label={t('fuel.fuelTypes.fields.grade')}
            error={fieldError('grade')}
            {...form.register('grade')}
          />
          <Controller
            control={form.control}
            name="factorClass"
            render={({ field }) => (
              <Select
                className={classes.wide}
                label={t('fuel.fuelTypes.fields.factorClass')}
                description={t('fuel.fuelTypes.fields.factorClassHint')}
                clearable
                placeholder={t('fuel.fuelTypes.unverified')}
                data={FACTOR_CLASSES.map((c) => ({
                  value: c,
                  label: t(`fuel.fuelTypes.factorClasses.${c}`),
                }))}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('factorClass')}
              />
            )}
          />
          <Textarea
            className={classes.wide}
            label={t('fuel.fuelTypes.fields.remarks')}
            minRows={2}
            autosize
            error={fieldError('remarks')}
            {...form.register('remarks')}
          />
          <Controller
            control={form.control}
            name="active"
            render={({ field }) => (
              <Switch
                className={classes.wide}
                label={t('fuel.fuelTypes.fields.active')}
                checked={field.value}
                onChange={(event) => field.onChange(event.currentTarget.checked)}
              />
            )}
          />
        </div>
        <div className={classes.dialogFooter}>
          <div className={classes.footerActions}>
            <Button variant="default" onClick={onClose}>
              {t('fuel.tanks.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              leftSection={<IconDeviceFloppy size={18} />}
            >
              {t('fuel.tanks.save')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
